/**
 * Letter-block file-type badges. The tree panel and the viewer header both
 * use these so the user gets a consistent visual signal (MD amber, PDF rose,
 * DOC blue, etc.) end-to-end through the open flow.
 *
 * @module file-agent/lib/file-badge
 */

export interface FileBadge {
  /** 2-3 character label rendered inside the letter-block. */
  label: string;
  /** Tailwind classes for background tint + foreground text. */
  className: string;
}

/** Resolve an extension to a colored badge. Falls through to a neutral zinc tint. */
export function getFileBadge(name: string): FileBadge {
  const ext = name.toLowerCase().split(".").pop() ?? "";
  switch (ext) {
    case "md":
    case "markdown":
    case "mdx":
      return { label: "MD", className: "bg-amber-500/15 text-amber-700" };
    case "pdf":
      return { label: "PDF", className: "bg-rose-500/10 text-rose-700" };
    case "doc":
    case "docx":
      return { label: "DOC", className: "bg-blue-500/10 text-blue-700" };
    case "xls":
    case "xlsx":
      return { label: "XLS", className: "bg-emerald-500/12 text-emerald-700" };
    case "csv":
      return { label: "CSV", className: "bg-emerald-500/12 text-emerald-700" };
    case "ppt":
    case "pptx":
      return { label: "PPT", className: "bg-orange-500/10 text-orange-700" };
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
    case "svg":
    case "bmp":
      return { label: ext.toUpperCase().slice(0, 3), className: "bg-violet-500/10 text-violet-700" };
    case "json":
      return { label: "{}", className: "bg-emerald-500/12 text-emerald-700" };
    case "html":
    case "htm":
      return { label: "HTM", className: "bg-orange-500/10 text-orange-700" };
    case "txt":
    case "log":
      return { label: "TXT", className: "bg-zinc-200 text-zinc-600" };
    default:
      return { label: ext.slice(0, 3).toUpperCase() || "···", className: "bg-zinc-200 text-zinc-600" };
  }
}
